/** The node's editable fields every environment has: description, kind and categories. */
import { For, Show, createSignal, type JSX } from "solid-js";
import type { Node } from "@protocol/Node";
import { useApp } from "../../app/context";
import { CategoryPicker } from "../../shared/CategoryPicker";

/** The one-line description, edited in place. */
export function NodeDescription(props: { node: () => Node }): JSX.Element {
  const n = props.node;
  const { actions } = useApp();
  /** Sends a change of this node to the core. */
  const update = (patch: Parameters<typeof actions.updateNode>[1]) => actions.updateNode(n().id, patch);
  const [editingNote, setEditingNote] = createSignal(false);
  let note!: HTMLTextAreaElement;
  return (
      <section class="grp">
        <h3>Description</h3>
        <Show
          when={editingNote()}
          fallback={
            <p class="note clickable" onClick={() => setEditingNote(true)}>
              {n().note || "Add a one-line description…"}
            </p>
          }
        >
          <textarea class="codeta" ref={note} rows="2" maxlength="300" value={n().note} />
          <div class="btnrow">
            <button type="button" class="btn primary" onClick={() => void update({ note: note.value.trim() }).then(() => setEditingNote(false))}>
              Save
            </button>
            <button type="button" class="btn" onClick={() => setEditingNote(false)}>
              Cancel
            </button>
          </div>
        </Show>
      </section>
  );
}

/** The kind (a select) and the categories (the graph's as toggles, or a new one named). */
export function NodeKindAndCategories(props: { node: () => Node }): JSX.Element {
  const n = props.node;
  const { view, graph, actions, kinds } = useApp();
  /** Sends a change of this node to the core. */
  const update = (patch: Parameters<typeof actions.updateNode>[1]) => actions.updateNode(n().id, patch);
  return (
      <section class="grp">
        <h3>Kind and categories</h3>
        <div class="stack">
          <select class="select" aria-label="Kind" id="node-kind" value={n().kind} onChange={(e) => void kinds.setNodeKind(n().id, e.currentTarget.value)}>
            <For each={kinds.nodes()}>
              {(k) => (
                <option value={k.id} selected={k.id === n().kind} title={k.note || undefined}>
                  {k.builtin || !k.like ? k.label : `${k.label} (${kinds.nodes().find((b) => b.id === k.like)?.label.toLowerCase() ?? k.like})`}
                </option>
              )}
            </For>
          </select>
          <CategoryPicker
            id="node-categories"
            value={n().categories}
            known={graph.categories()}
            onChange={(categories) => {
              void update({ categories });
              // A category just chosen is shown, so the node does not vanish from the graph.
              for (const c of categories.length ? categories : [""]) view.showCategory(c);
            }}
          />
        </div>
      </section>
  );
}
